/**
 * Supabase Configuration
 * Centralized configuration for Supabase client settings
 */

export const supabaseConfig = {
  // Authentication settings
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce' as const,
    // Redirect URLs for different environments. Production no longer hardcodes
    // app.icrop.net — callers compute context-aware URLs via
    // buildAdminUrl / buildWorkspaceUrl. VITE_APP_URL stays as an optional
    // override for testing.
    redirectUrls: {
      development: 'http://localhost:5173',
      production: import.meta.env.VITE_APP_URL || '',
    },
    // Session storage key (Phase 1: prefixed `sb-` to invalidate legacy
    // localStorage sessions — the cookie is now scoped to .icrop.net so any
    // pre-Phase-1 session is unsafe to keep).
    storageKey: 'sb-icrop-auth',
    // Phase 1: share the session across subdomains so logging in at
    // app.icrop.net keeps you signed in on summit.icrop.net.
    cookieOptions: {
      domain: '.icrop.net',
      sameSite: 'lax' as const,
      secure: true,
    },
  },

  // Database settings
  db: {
    // Default schema
    schema: 'public',
    // Connection settings
    connection: {
      // Enable connection pooling
      pooling: true,
      // Connection timeout
      timeout: 30000,
    },
  },

  // Storage settings
  storage: {
    // Default bucket for documents
    defaultBucket: 'documents',
    // File upload settings
    upload: {
      maxFileSize: 50 * 1024 * 1024, // 50MB
      allowedMimeTypes: [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/webp',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ],
      // File naming convention
      fileNaming: {
        includeTimestamp: true,
        includeAccountId: true,
        includeUserId: false,
      },
    },
  },

  // Real-time settings
  realtime: {
    // Enable real-time subscriptions
    enabled: true,
    // Heartbeat interval
    heartbeatIntervalMs: 30000,
    // Reconnect settings
    reconnect: {
      enabled: true,
      maxRetries: 5,
      retryDelayMs: 1000,
    },
  },

  // API settings
  api: {
    // Request timeout
    timeout: 30000,
    // Retry settings
    retry: {
      enabled: true,
      maxRetries: 3,
      retryDelayMs: 1000,
    },
    // Headers
    headers: {
      'X-Client-Info': 'icrop-frontend',
      'X-Client-Version': '1.0.0',
    },
  },

  // Feature flags
  features: {
    // Enable real-time document updates
    realtimeDocuments: true,
    // Enable file upload progress tracking
    uploadProgress: true,
    // Enable offline support
    offlineSupport: false,
    // Enable advanced search
    advancedSearch: true,
    // Enable document preview
    documentPreview: true,
  },

  // Environment-specific settings
  environment: {
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
    // Enable debug logging in development
    enableDebugLogging: import.meta.env.DEV,
    // Enable performance monitoring
    enablePerformanceMonitoring: import.meta.env.PROD,
  },
} as const

/**
 * Get environment-specific redirect URL
 */
export function getRedirectUrl(): string {
  const { isDevelopment } = supabaseConfig.environment
  return isDevelopment 
    ? supabaseConfig.auth.redirectUrls.development
    : supabaseConfig.auth.redirectUrls.production
}

/**
 * Check if a file type is allowed for upload
 */
export function isAllowedFileType(mimeType: string): boolean {
  return (supabaseConfig.storage.upload.allowedMimeTypes as readonly string[]).includes(mimeType)
}

/**
 * Check if file size is within limits
 */
export function isValidFileSize(size: number): boolean {
  return size <= supabaseConfig.storage.upload.maxFileSize
}

/**
 * Generate file path for storage
 */
export function generateFilePath(
  accountId: string,
  organizationId: string,
  fileName: string,
  includeTimestamp = supabaseConfig.storage.upload.fileNaming.includeTimestamp
): string {
  const timestamp = includeTimestamp ? Date.now() : ''
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_')

  return `${accountId}/${organizationId}/${timestamp ? `${timestamp}_` : ''}${sanitizedFileName}`
}

/**
 * Cross-subdomain cookie storage for the Supabase auth session.
 *
 * `@supabase/supabase-js` v2 expects a `storage` adapter with
 * `getItem / setItem / removeItem`. We back it with `document.cookie` so the
 * session is shared across `*.icrop.net` subdomains. On `localhost` the cookie
 * `domain` attribute is omitted (a `.icrop.net` cookie would never apply to
 * `localhost`), which keeps dev sessions per-host but unblocks production.
 *
 * Sessions typically fit in ~3 KB. Browsers cap a single cookie at ~4 KB, so a
 * non-chunked store is enough for Phase 1. If a future session ever exceeds the
 * cookie size limit, the silent set failure will force a re-login — acceptable.
 */
function isProductionHost(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.hostname.endsWith('icrop.net')
}

function buildCookieAttributes(maxAgeSeconds = 60 * 60 * 24 * 365): string {
  const opts = supabaseConfig.auth.cookieOptions
  const parts: string[] = []
  parts.push(`Path=/`)
  parts.push(`Max-Age=${maxAgeSeconds}`)
  parts.push(`SameSite=${opts.sameSite.charAt(0).toUpperCase()}${opts.sameSite.slice(1)}`)
  if (isProductionHost()) {
    parts.push(`Domain=${opts.domain}`)
    if (opts.secure) parts.push('Secure')
  }
  return parts.join('; ')
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const pattern = new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`)
  const match = document.cookie.match(pattern)
  return match ? decodeURIComponent(match[1]) : null
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=${encodeURIComponent(value)}; ${buildCookieAttributes()}`
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=; ${buildCookieAttributes(0)}`
}

export const crossSubdomainCookieStorage = {
  getItem(key: string): string | null {
    return readCookie(key)
  },
  setItem(key: string, value: string): void {
    writeCookie(key, value)
  },
  removeItem(key: string): void {
    deleteCookie(key)
  },
}

/**
 * Pass-through auth lock — disables the Web Locks API (`navigator.locks`)
 * coordination that supabase-js enables by default.
 *
 * The navigator lock is shared across every same-origin tab. When any holder
 * stalls — a hung token refresh, a tab killed mid-operation, an old bundle
 * deadlocked on its own callback — the lock is never released and EVERY
 * supabase call in EVERY tab on that origin either times out or aborts with
 * `AbortError: signal is aborted without reason`. That failure mode bricked
 * `app.icrop.net` and could only be cleared by fully quitting the browser.
 *
 * Running auth operations without the cross-tab lock is the pre-v2.39
 * supabase-js behavior. In-tab concurrent token refreshes are still deduped
 * by auth-js's own `refreshingDeferred` guard, and Supabase's refresh-token
 * reuse grace window absorbs the rare cross-tab race — so the only thing
 * given up is cross-tab refresh coordination, an acceptable trade against a
 * hard, browser-restart-only outage.
 */
const passthroughAuthLock = async <R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => fn()

/**
 * Get Supabase client configuration
 */
export function getSupabaseClientConfig() {
  const { autoRefreshToken, persistSession, detectSessionInUrl, flowType, storageKey } =
    supabaseConfig.auth
  const authConfig = { autoRefreshToken, persistSession, detectSessionInUrl, flowType, storageKey }
  return {
    auth: {
      ...authConfig,
      storage: crossSubdomainCookieStorage,
      lock: passthroughAuthLock,
    },
    global: {
      headers: supabaseConfig.api.headers,
    },
    realtime: supabaseConfig.realtime.enabled ? {
      heartbeatIntervalMs: supabaseConfig.realtime.heartbeatIntervalMs,
    } : undefined,
  }
}